// @flow
import { useReactiveVar } from '@apollo/client';
import { equals } from 'ramda';

import { aiModalChangesVar } from '../../utils/apollo-cache';

import type { ProductChange, ProductChangeInput } from '../../typings/product';

const getIdFromInput = (input: ProductChangeInput): string => {
  const { productId, variationId, showcaseId, fieldName } = input;
  return `${productId}|${variationId || ''}|${showcaseId || ''}|${fieldName}`;
};

const formatInput = (input: ProductChangeInput): ProductChange => ({
  ...input,
  variationId: input.variationId || '',
  showcaseId: input.showcaseId || '',
  id: getIdFromInput(input),
  ignore: input.value === input.initialValue,
});

function getChangesResult(
  changes: ProductChange[],
  input: ProductChangeInput
): ProductChange[] {
  const newChange = formatInput(input);
  const changeResult = [...changes];

  const index = changeResult.findIndex((change) => change.id === newChange.id);
  if (index !== -1) {
    changeResult[index] = newChange;
  } else {
    changeResult.push(newChange);
  }

  return changeResult;
}

/**
 * AI Products Modal 专用的 client change hook
 *
 * 与 stock-table 的 useClientChange 的区别：
 * 1. 写入 aiModalChangesVar 而不是全局 changesVar
 * 2. 支持 mass update（批量编辑）
 * 3. 不需要 filters（AI Modal 有固定的产品列表）
 * 4. 简化的实现，专注于 AI Modal 的场景
 */
export default function useAiModalClientChange(
  fieldName: string,
  initialValue: any,
  productId: string,
  variationId?: string,
  showcaseId?: string,
  checkedProducts?: string[],
  checkedVariations?: Set<string>
): {| changed: boolean, onChange: (changedValue: any) => void, value: any |} {
  const changes = useReactiveVar(aiModalChangesVar);

  const id = getIdFromInput({
    productId,
    variationId,
    showcaseId,
    fieldName,
  });

  const change = changes.find((c) => c.id === id);

  const onChange = (changedValue: any) => {
    // Check if this is a mass update (productId === 'mass')
    const isMassUpdate = productId === 'mass';

    if (isMassUpdate && checkedProducts && checkedVariations) {
      // Mass update: expand to individual product changes
      let newChanges = [...changes];

      // Create individual changes for each checked product
      const massChanges: ProductChange[] = [];

      // Apply to all checked products (product-level changes)
      // Use 'unclear' as initialValue for all products in mass update
      // This ensures all products create changes and display switcher in sync
      checkedProducts.forEach((pId) => {
        const massChangeInput: ProductChangeInput = {
          productId: pId,
          variationId: '',
          showcaseId,
          fieldName,
          value: changedValue,
          initialValue: 'unclear', // Uniform initialValue for mass update
        };

        massChanges.push(formatInput(massChangeInput));
      });

      // Remove old mass changes for these products/showcase/field
      const freshChangesWithoutMassUpdate = newChanges.filter(
        (c) => !massChanges.find((massChange) => massChange.id === c.id)
      );

      // Add new mass changes
      newChanges = [...freshChangesWithoutMassUpdate, ...massChanges];

      // Also add/update the mass change record itself for tracking
      const massChangeRecord: ProductChangeInput = {
        productId: 'mass',
        variationId: '',
        showcaseId,
        fieldName,
        value: changedValue,
        initialValue,
      };
      newChanges = getChangesResult(newChanges, massChangeRecord);

      if (!equals(changes, newChanges)) {
        aiModalChangesVar(newChanges);
      }
    } else {
      // Single product update
      const changeData = {
        productId,
        variationId,
        showcaseId,
        fieldName,
        value: changedValue,
        initialValue,
      };

      const newChanges = getChangesResult(changes, changeData);

      if (!equals(changes, newChanges)) {
        aiModalChangesVar(newChanges);
      }
    }

    // 可选：添加 Analytics 追踪
    // ANALYTICS.change(fieldName, false);
  };

  const value = !change || change.ignore ? initialValue : change.value;

  return {
    onChange,
    changed: change && !change.ignore,
    value,
  };
}
